import { Area, AreaChart, XAxis, YAxis, CartesianGrid } from "recharts";

import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import {
    ChartConfig,
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
} from "@/components/ui/chart";
import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabaseClient";

type PriceData = {
    price: number;
    density: number;
    fill: string;
};

const chartConfig = {
    priceDistribution: {
        label: "Price Distribution",
        color: "hsl(var(--primary))",
    },
} satisfies ChartConfig;

export function ProductPricesCard() {
    const [priceData, setPriceData] = useState<PriceData[]>([]);
    const [productCount, setProductCount] = useState(0);
    const { session } = useAuth();

    useEffect(() => {
        async function fetchProducts() {
            const { data: products } = await supabase
                .from("products")
                .select("price")
                .order("price", { ascending: true });

            if (products) {
                // Extract prices and filter out nulls
                const prices = products
                    .map((product) => product.price)
                    .filter((price): price is number => price !== null);

                if (prices.length === 0) {
                    setPriceData([]);
                    return;
                }

                // Find min and max prices
                const minPrice = Math.min(...prices);
                const maxPrice = Math.max(...prices);

                // Create KDE points
                const kdePoints = calculateKDE(prices, minPrice, maxPrice);

                setPriceData(kdePoints);
                setProductCount(prices.length);
            }
        }

        fetchProducts();
    }, [session]);

    // Calculate Kernel Density Estimation
    function calculateKDE(
        prices: number[],
        minPrice: number,
        maxPrice: number
    ): PriceData[] {
        // Number of points to evaluate the density
        const numPoints = 100;

        // Bandwidth selection (rule of thumb)
        const bandwidth =
            1.06 * standardDeviation(prices) * Math.pow(prices.length, -0.2);

        // Generate evaluation points
        const step = (maxPrice - minPrice) / (numPoints - 1);
        const evaluationPoints = Array.from(
            { length: numPoints },
            (_, i) => minPrice + i * step
        );

        // Calculate density at each point
        const densities = evaluationPoints.map((point) => {
            // Gaussian kernel
            const density =
                prices.reduce((sum, price) => {
                    const z = (point - price) / bandwidth;
                    // Gaussian kernel function: (1/√(2π)) * e^(-z²/2)
                    const kernelValue =
                        (1 / Math.sqrt(2 * Math.PI)) * Math.exp(-0.5 * z * z);
                    return sum + kernelValue;
                }, 0) /
                (prices.length * bandwidth);

            return density;
        });

        // Scale densities for better visualization
        const maxDensity = Math.max(...densities);
        const scaledDensities = densities.map(
            (d) => ((d / maxDensity) * prices.length) / 4
        );

        // Format points for chart
        return evaluationPoints.map((price, i) => {
            // Format price for display - ensure we have labels for the X-axis
            const formattedPrice =
                price >= 1000
                    ? `£${(price / 1000).toFixed(1)}k`
                    : `£${price.toFixed(0)}`;

            // Only include some labels to avoid overcrowding
            // Always set priceLabel, but only show text for every 5th point
            const priceLabel = i % 5 === 0 ? formattedPrice : "";

            return {
                price,
                priceLabel, // This is the key for XAxis
                density: scaledDensities[i],
                fill: chartConfig.priceDistribution.color,
            };
        });
    }

    // Helper function to calculate standard deviation
    function standardDeviation(values: number[]): number {
        const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
        const squareDiffs = values.map((value) => {
            const diff = value - avg;
            return diff * diff;
        });
        const avgSquareDiff =
            squareDiffs.reduce((sum, val) => sum + val, 0) / values.length;
        return Math.sqrt(avgSquareDiff);
    }

    return (
        <Card>
            <CardHeader>
                <CardTitle>Product Prices</CardTitle>
                <CardDescription>
                    Distribution of product prices across your catalog
                </CardDescription>
            </CardHeader>
            <div className="relative">
                <CardContent>
                    <ChartContainer config={chartConfig}>
                        <AreaChart
                            accessibilityLayer
                            data={priceData}
                            margin={{
                                left: 0,
                                bottom: 0,
                                right: 30,
                                top: 20,
                            }}
                        >
                            <CartesianGrid
                                vertical={true}
                                horizontal={true}
                                strokeDasharray="3 3"
                            />
                            <XAxis
                                dataKey="priceLabel"
                                tickLine={true}
                                axisLine={true}
                                angle={-45}
                                textAnchor="end"
                                height={70}
                                tick={{ fontSize: 12 }}
                                label={{
                                    value: "Price (£)",
                                    position: "insideBottom",
                                }}
                                // Force ticks to appear
                                interval={0}
                                // Explicitly set tick count
                                ticks={priceData
                                    .filter((d, i) => i % 10 === 0)
                                    .map((d) => d.priceLabel)}
                            />
                            <YAxis
                                tickLine={false}
                                axisLine={true}
                                tickMargin={10}
                                tick={false}
                                label={{
                                    value: "Proportion",
                                    angle: -90,
                                }}
                            />
                            <ChartTooltip
                                cursor={false}
                                content={
                                    <ChartTooltipContent
                                        formatter={(value, name, props) => {
                                            const price = props.payload?.price;
                                            return price
                                                ? `£${price.toFixed(2)}`
                                                : "";
                                        }}
                                        labelFormatter={() => "Price Point"}
                                    />
                                }
                            />
                            <Area
                                type="monotone"
                                dataKey="density"
                                stroke={chartConfig.priceDistribution.color}
                                fill={chartConfig.priceDistribution.color}
                                fillOpacity={0.6}
                                name="Density"
                            />
                        </AreaChart>
                    </ChartContainer>
                </CardContent>
                <CardFooter className="flex-col items-start gap-2">
                    <div className="flex items-center gap-2 font-medium">
                        Price distribution
                    </div>
                    <div className="text-sm text-muted-foreground">
                        Showing the distribution of product prices across your
                        catalog of {productCount} products
                    </div>
                </CardFooter>
            </div>
        </Card>
    );
}
